import React, { Component } from 'react';

import { Collapsible } from 'components/v2/Collapsible';
import { themes } from 'kb-shared';
import styled from 'styled-components';

import { Theme } from './TabNavigator';
import CollapseIcon from '../SVG/Collapse';
import ExpandIcon from '../SVG/Expand';

const { colors } = themes;

const NavigatorContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

interface ItemWrapperProps {
  $selected: boolean;
  $selectedColor: string;
  $deselectedColor: string;
}

const ItemWrapper = styled.div<ItemWrapperProps>`
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  background-color: ${({ $selected, $selectedColor, $deselectedColor }) =>
    $selected ? $selectedColor : $deselectedColor};
  transition: background-color 200ms ease-out;
`;

const ItemHeader = styled.a`
  align-items: center;
  display: flex;
  padding: 18px 20px;
  justify-content: space-between;
`;

interface ItemLabelProps {
  $selected: boolean;
  $selectedColor: string;
  $deselectedColor: string;
}

const ItemLabel = styled.h2<ItemLabelProps>`
  ${({ theme }) => theme.typos.sectionHeader};
  font-size: 18px;
  font-weight: bold;
  min-width: 140px;
  padding-right: 10px;
  overflow: hidden;
  color: ${({ $selected, $selectedColor, $deselectedColor }) =>
    $selected ? $selectedColor : $deselectedColor};
  transition: color 200ms ease-out;
`;

const ExpandCollapseIcon = styled.div`
  width: 20px;
  height: 20px;
`;

interface ContentContainerProps {
  $selected: boolean;
  $selectedColor: string;
  $deselectedColor: string;
}

const ContentContainer = styled.div<ContentContainerProps>`
  ${({ theme }) => theme.grid.container};
  background-color: ${({ $selected, $selectedColor, $deselectedColor }) =>
    $selected ? $selectedColor : $deselectedColor};
  transition: background-color 200ms ease-out;
`;

type Props<T> = {
  currentIndex: number;
  items: Array<T>;
  getSectionTitle: (index: number, item: T) => string;
  renderContent: (index: number, item: T) => React.ReactNode | null;
  getUniqueKey: (index: number, item: T) => string;
  onSelect: (index: number, item: T) => void;
  theme: Theme;
};

type State = {
  isOpen: boolean;
};

export default class MobileNavigator<T> extends Component<Props<T>, State> {
  constructor(props: Props<T>) {
    super(props);
    this.state = { isOpen: true };
  }

  render() {
    const { currentIndex, items, onSelect, theme } = this.props;
    const { isOpen } = this.state;

    return (
      <NavigatorContainer>
        {items.map((item, index) => {
          const selected = index === currentIndex && isOpen;
          const title = this.props.getSectionTitle(index, item);
          const itemKey = this.props.getUniqueKey(index, item);
          return (
            <ItemWrapper
              data-testid={itemKey}
              key={itemKey}
              $selected={selected}
              $selectedColor={theme.tab.selectedBG}
              $deselectedColor={theme.tab.deselectedBG}
            >
              <ItemHeader
                onClick={e => {
                  e.preventDefault();
                  if (isOpen && selected) {
                    this.setState({ isOpen: false });
                  } else {
                    this.setState({ isOpen: true });
                  }
                  onSelect(index, item);
                }}
              >
                <ItemLabel
                  $selected={selected}
                  $selectedColor={theme.tab.selectedTitle}
                  $deselectedColor={theme.tab.deselectedTitle}
                >
                  {title}
                </ItemLabel>
                <ExpandCollapseIcon>
                  {selected ? (
                    <CollapseIcon color={colors.neutral.lavenderWhite} />
                  ) : (
                    <ExpandIcon color={colors.neutral.primaryNavy} />
                  )}
                </ExpandCollapseIcon>
              </ItemHeader>
              <Collapsible visible={selected}>
                <ContentContainer
                  $selected={selected}
                  $selectedColor={theme.content.selectedBG}
                  $deselectedColor={theme.content.deselectedBG}
                  style={{ pointerEvents: selected ? 'auto' : 'none' }}
                >
                  {this.props.renderContent(index, item)}
                </ContentContainer>
              </Collapsible>
            </ItemWrapper>
          );
        })}
      </NavigatorContainer>
    );
  }
}
